import {
    DialogClose,
} from '@/components/ui/dialog';
import { BaseModal } from '@/shared/ui/BaseModal.jsx';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { useTranslation } from 'react-i18next';
import { usePatchRequest } from '@/shared/hooks/requests.js';
import { FILE_VISIBILITY, NODE } from '@/shared/utils/urls.js';
import { Lock, Globe } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const VISIBILITY_OPTIONS = [
    { value: 'private', label: 'my-docs:visibility.private', icon: Lock, description: 'my-docs:visibility.privateDesc' },
    { value: 'public', label: 'my-docs:visibility.public', icon: Globe, description: 'my-docs:visibility.publicDesc' },
];

export const ChangeVisibilityModal = ({ open, onOpenChange, file, isFolder = false, setData }) => {
    const { t } = useTranslation();
    const [selectedVisibility, setSelectedVisibility] = useState(file?.visibility || 'private');

    // Files expose a dedicated PATCH visibility endpoint; folders take the
    // unified PATCH node endpoint with a visibility field.
    const uuid = file?.uuid;
    const fileVisibility = usePatchRequest({ url: uuid ? FILE_VISIBILITY.replace('{file_uuid}', uuid) : null });
    const folderVisibility = usePatchRequest({ url: uuid ? NODE.replace('{node_uuid}', uuid) : null });
    const updateVisibility = isFolder ? folderVisibility : fileVisibility;

    const handleUpdate = async () => {
        const { success } = await updateVisibility.request({
            data: { visibility: selectedVisibility }
        });

        if (success) {
            toast.success(t('my-docs:visibility.success'));
            onOpenChange(false);
            if (setData) setData();
        }
    };

    return (
        <BaseModal
            open={open}
            onOpenChange={onOpenChange}
            title={t('my-docs:visibility.title')}
            description={t('my-docs:visibility.description')}
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        className="font-medium px-4"
                    >
                        {t('my-docs:btn.cancel')}
                    </Button>
                    <Button
                        className="font-semibold text-white px-8"
                        style={{
                            background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                            border: 'none',
                        }}
                        onClick={handleUpdate}
                        disabled={updateVisibility.loading}
                    >
                        {updateVisibility.loading ? (
                            <LoadingSpinner color="#fff" />
                        ) : (
                            t('my-docs:btn.save')
                        )}
                    </Button>
                </div>
            }
        >
            <div className="space-y-6 pt-2">
                <div className="flex items-center gap-2 p-3 bg-muted/40 rounded-md border border-border/50">
                    <span className="text-sm font-semibold text-muted-foreground">{t('my-docs:share.fileName')}:</span>
                    <span className="text-sm font-bold text-foreground truncate max-w-[300px]" title={file?.name}>
                        {file?.name}
                    </span>
                </div>

                <div className="grid grid-cols-1 gap-3">
                    {VISIBILITY_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const isActive = selectedVisibility === option.value;

                        return (
                            <button
                                key={option.value}
                                onClick={() => setSelectedVisibility(option.value)}
                                className={cn(
                                    'group relative flex items-start gap-4 p-4 rounded-md border-2 text-left transition-all duration-300 outline-none cursor-pointer',
                                    isActive
                                        ? 'border-primary bg-primary/5 ring-4 ring-primary/5'
                                        : 'border-border bg-card hover:border-primary/30 hover:bg-accent/50'
                                )}
                            >
                                <div className={cn(
                                    "p-2.5 rounded-md transition-all duration-300",
                                    isActive
                                        ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30"
                                        : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                                )}>
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className={cn(
                                        "font-bold text-[15px] transition-colors",
                                        isActive ? "text-primary" : "text-foreground group-hover:text-primary"
                                    )}>
                                        {t(option.label)}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1 leading-relaxed line-clamp-2">
                                        {t(option.description)}
                                    </div>
                                </div>
                                <div className={cn(
                                    "h-5 w-5 rounded-full border-2 mt-2 transition-all duration-300 flex items-center justify-center shrink-0",
                                    isActive ? "border-primary bg-primary" : "border-muted-foreground/30"
                                )}>
                                    {isActive && <div className="h-2 w-2 rounded-full bg-white shadow-sm" />}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </BaseModal>
    );
};
